'use client';

import { useEffect, useLayoutEffect, useRef } from 'react';

import { usePathname, useRouter } from 'next/navigation';

import { pause, resume } from '@noriginmedia/norigin-spatial-navigation';
import { useQueryClient } from '@tanstack/react-query';

import { useGlobalConfig } from '@/components/GlobalContext';
import { PageState } from '@/components/GlobalContext';

import { Keys, getSystem } from '@/system';

export const NAVIGATION_START_EVENT = 'app:navigation:start';
export const NAVIGATION_END_EVENT = 'app:navigation:end';
let _isNavigating = false;
export const isNavigating = () => _isNavigating;

// Back pressed mid-navigation, held until the next page's nav mounts
let _navOpenQueued = false;

export function useBackDuringNavigation(openNav: () => void) {
  const openOnMountRef = useRef(false);

  // Layout effect so it runs before usePageState's mount effect drops the queue
  useLayoutEffect(() => {
    openOnMountRef.current = _navOpenQueued;
    _navOpenQueued = false;
  }, []);

  useEffect(() => {
    if (openOnMountRef.current) openNav();

    // Window capture runs before the pages' document Back listeners
    const queueBack = (event: KeyboardEvent) => {
      if (getSystem().key(event) !== Keys.BACK || !_isNavigating) return;
      _navOpenQueued = true;
      event.preventDefault();
      event.stopPropagation();
    };
    window.addEventListener('keydown', queueBack, true);
    return () => window.removeEventListener('keydown', queueBack, true);
  }, []);
}

/**
 * App navigation helpers that wrap the Next.js router and keep a stack of
 * visited pages, so a page can restore its focus and scroll when returned to.
 */
function usePageState() {
  const router = useRouter();
  const pathname = usePathname();
  const queryClient = useQueryClient();

  // A new route has mounted: end the navigation and resume spatial navigation.
  useEffect(() => {
    _isNavigating = false;
    _navOpenQueued = false;
    resume();
    window.dispatchEvent(new CustomEvent(NAVIGATION_END_EVENT));
  }, [pathname]);

  const { setCurrentPageState, currentPage, pageStack, setPageStack } = useGlobalConfig();

  const clearPageState = () => {
    setPageStack([]);
    setCurrentPageState(undefined);
  };

  return {
    /**
     * Navigate forward to `url`, saving the page being left on the stack.
     * Spatial navigation is paused until the next page mounts.
     * @param url Route to navigate to.
     * @param pageName Name of the page being left, used to restore it on back.
     * @param state State to restore when coming back (focus key, scroll, etc.).
     */
    push: (url: string, pageName: string, state?: any) => {
      pause();
      const newPageState: PageState = { page: pageName, state };

      setPageStack((prevStack) => [...prevStack, newPageState]);

      _isNavigating = true;
      window.dispatchEvent(new CustomEvent(NAVIGATION_START_EVENT));
      // Disable Next.js automatic scroll restoration to use our custom scroll logic
      router.push(url, { scroll: false });
    },

    /**
     * Go back through browser history, popping the same number of stack entries
     * and making the page landed on the `currentPage` for restoring state.
     * With an empty stack, also clears cached search queries.
     * @param numberOfPages How many pages to go back. Defaults to 1.
     * @param state Extra state merged into the restored page's saved state.
     */
    back: (numberOfPages: number = 1, state?: any) => {
      if (numberOfPages < 1) {
        return;
      }

      setPageStack((prevStack) => {
        if (prevStack.length === 0) {
          queryClient.removeQueries({
            predicate: (query) => query.queryKey[0] === 'search',
          });
          return [];
        }

        const removeStart = Math.max(0, prevStack.length - numberOfPages);
        const nextStack = prevStack.slice(0, removeStart);
        const removedPages = prevStack.slice(removeStart);
        const pageToRestoreTo =
          numberOfPages <= prevStack.length ? removedPages[0] : undefined;

        if (pageToRestoreTo) {
          window.dispatchEvent(new CustomEvent(NAVIGATION_START_EVENT));
          setCurrentPageState({
            ...pageToRestoreTo,
            state: { ...pageToRestoreTo.state, ...state },
          });
        }

        return nextStack;
      });

      window.history.go(-numberOfPages);
    },

    /**
     * Clear the page stack and replace the current route with `url`,
     * so the user can't go back to where they were.
     */
    replacePageState: (url: string) => {
      clearPageState();
      router.replace(url);
    },

    /** Empty the page stack and unset the current page state. */
    clearPageState,

    /** Overwrite the page stack with `pageStack`. */
    updatePageStack: (pageStack: PageState[]) => {
      setPageStack(pageStack);
    },

    /**
     * Replace the current route with `url` and overwrite the top stack entry
     * with `pageName`/`state`, without adding a history entry.
     */
    replaceCurrentPage: (url: string, pageName: string, state?: any) => {
      setPageStack((prevStack) => {
        const newStack = [...prevStack];
        newStack[newStack.length - 1] = { page: pageName, state };
        return newStack;
      });
      router.replace(url);
    },

    /** The Next.js router, for anything the helpers above don't cover. */
    router,
    /** The page being restored after `back`, with its saved state. */
    currentPage,
    /** Pages saved by `push`, oldest first. */
    pageStack,
  };
}

export default usePageState;
